import type { FC, ReactNode } from 'react'
import type { PreviewArc, PreviewFlag, PreviewFrameInfo, PreviewTag } from '@/lib/cloud/preview'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

type BudgetName = 'preview' | 'thumb' | 'hero' | 'globe' | 'small'

interface PointCloudProps {
    shape: string
    label: string
    budget?: BudgetName
    rotate?: 'always' | 'hover' | 'none'
    rotateSpeed?: number
    hoverBoost?: number
    interactive?: boolean
    effects?: boolean
    frame?: boolean
    grid?: boolean
    spin?: boolean
    tilt?: number
    yaw?: number
    pitch?: number
    fitWidth?: number
    viewShift?: number
    sizeScale?: number
    background?: number
    tags?: Array<PreviewTag>
    flags?: Array<PreviewFlag>
    arc?: PreviewArc
    grow?: boolean
    hover?: boolean
    crosshair?: boolean
    onFrame?: (info: PreviewFrameInfo) => void
    className?: string
    children?: ReactNode
}

interface PreviewHandle {
    setShape: (key: string) => void
    setHover: (hover: boolean) => void
    dispose: () => void
}

/**
 * Lazily creates a point-cloud preview once its box comes near the viewport.
 */
export const PointCloud: FC<PointCloudProps> = ({
    shape,
    label,
    budget = 'preview',
    rotate = 'hover',
    rotateSpeed,
    hoverBoost,
    interactive = false,
    effects = true,
    frame = false,
    grid = false,
    spin = false,
    tilt,
    yaw,
    pitch,
    fitWidth,
    viewShift,
    sizeScale,
    background,
    tags,
    flags,
    arc,
    grow = false,
    hover,
    crosshair = true,
    onFrame,
    className,
    children
}): ReactNode => {
    const containerRef = useRef<HTMLDivElement>(null)
    const labelRef = useRef<HTMLDivElement>(null)
    const previewRef = useRef<PreviewHandle | null>(null)
    const onFrameRef = useRef(onFrame)
    const shapeRef = useRef(shape)
    const [failed, setFailed] = useState(false)

    onFrameRef.current = onFrame
    shapeRef.current = shape

    useEffect(() => {
        const container = containerRef.current

        if (!container) return

        let cancelled = false
        let preview: PreviewHandle | null = null

        const observer = new IntersectionObserver(entries => {
            if (!entries.some(entry => entry.isIntersecting)) return

            observer.disconnect()

            import('@/lib/cloud/preview')
                .then(({ BUDGETS, CloudPreview }) => {
                    if (cancelled || !containerRef.current) return

                    try {
                        preview = new CloudPreview(containerRef.current, {
                            shape: shapeRef.current,
                            budget: BUDGETS[budget],
                            rotate,
                            rotateSpeed,
                            hoverBoost,
                            interactive,
                            effects,
                            frame,
                            grid,
                            spin,
                            tilt,
                            yaw,
                            pitch,
                            fitWidth,
                            viewShift,
                            sizeScale,
                            background,
                            labelRoot: labelRef.current,
                            tags,
                            flags,
                            arc,
                            grow,
                            onFrame: info => onFrameRef.current?.(info)
                        })
                        previewRef.current = preview
                    }
                    catch (error) {
                        console.error('Point cloud preview could not start:', error instanceof Error ? error.message : error)
                        setFailed(true)
                    }
                })
                .catch(() => setFailed(true))
        }, { rootMargin: '320px' })

        observer.observe(container)

        return () => {
            cancelled = true
            observer.disconnect()
            preview?.dispose()
            previewRef.current = null
        }
        // The preview is created once per mount; shape and hover changes are pushed below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        previewRef.current?.setShape(shape)
    }, [shape])

    useEffect(() => {
        if (hover === undefined) return

        previewRef.current?.setHover(hover)
    }, [hover])

    return (
        <div
            ref={containerRef}
            role='img'
            aria-label={label}
            data-crosshair={crosshair ? '' : undefined}
            className={cn('relative overflow-hidden bg-background', className)}
        >
            <div ref={labelRef} className='cf-labels z-[1]' aria-hidden='true' />

            {failed && (
                <div className='absolute inset-0 z-[2] grid place-items-center px-4'>
                    <p className='hud text-center text-mute'>
                        3D view unavailable in this browser
                    </p>
                </div>
            )}

            {children}
        </div>
    )
}
